import { useState, useRef } from "react";
import type { Initiative } from "@/types";
import { StatusDot } from "@/components/ui/Badge";
import { LinkIcon, ChatBubbleLeftIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import { ICON_MAP } from "@/components/modals/IconPicker";
import { useAppStore } from "@/stores/appStore";
import { formatMonthLabel } from "@/utils/monthLabel";
import { formatDateRange } from "@/utils/dateRange";
import { MonthLabel } from "@/components/ui/MonthLabel";

interface KanbanCardProps {
  initiative: Initiative;
  onClick?: () => void;
  /** When true the card can be dragged to another column (owner or admin). */
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function KanbanCard({ initiative, onClick, draggable = false, onDragStart, onDragEnd }: KanbanCardProps) {
  const quarters = useAppStore((s) => s.quarters);
  const classification = (initiative as any).classification;
  const status = (initiative as any).status;
  const owner = (initiative as any).user;
  const outgoingCount = (initiative as any).dependencies?.length || 0;
  const reverseCount = (initiative as any)._count?.dependents || 0;
  const hasDependencies = outgoingCount > 0 || reverseCount > 0;
  const dependencyTooltip =
    outgoingCount > 0 && reverseCount > 0
      ? `Depends on ${outgoingCount} · ${reverseCount} depend on this`
      : outgoingCount > 0
        ? `Depends on ${outgoingCount} initiative${outgoingCount === 1 ? "" : "s"}`
        : `${reverseCount} initiative${reverseCount === 1 ? "" : "s"} depend on this`;
  const commentCount = (initiative as any)._count?.comments || 0;
  const isStarred = (initiative as any).isStarred === true;

  const titleRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  // Tags flagged to display as an icon on the card
  const displayTags = (initiative as any).tags?.filter(
    (initTag: any) => initTag.tag?.displayAsIcon && initTag.tag?.icon
  ) || [];

  // Measure on hover only — avoids one ResizeObserver per card at scale.
  const handleTitleMouseEnter = () => {
    const el = titleRef.current;
    if (el && el.scrollWidth > el.clientWidth) setShowTooltip(true);
  };

  const color = classification?.color || "#3B82F6";

  // Resolve a quarter key to its month label; null when unset.
  const labelFor = (key?: string | null): string | null => {
    if (!key) return null;
    const q = quarters.find((qq) => qq.key === key);
    return q ? formatMonthLabel(q.fullLabel) : key;
  };
  const startLabel = labelFor((initiative as any).startQuarter);
  const endLabel = labelFor((initiative as any).endQuarter);

  // Date-mode initiatives show their exact start/end range; month-mode records keep the mapped
  // Start/End month labels.
  const dateRange =
    (initiative as any).datePrecision === "day"
      ? formatDateRange((initiative as any).startDate, (initiative as any).endDate)
      : null;

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={`group relative rounded-lg bg-background-primary border border-gray-200 shadow-sm px-3 py-2.5 transition-all hover:shadow-md ${
        draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
      }`}
      style={{
        borderLeftWidth: "3px",
        borderLeftColor: color,
        backgroundColor: `${color}0A`,
      }}
    >
      {/* Title row */}
      <div className="flex items-start gap-1.5 min-w-0">
        <span
          ref={titleRef}
          className="font-medium text-text-primary text-sm truncate min-w-0 flex-1"
          onMouseEnter={handleTitleMouseEnter}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {initiative.title}
        </span>
        {isStarred && <StarSolid className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />}

        {/* Title tooltip — only when truncated */}
        {showTooltip && (
          <div className="absolute left-0 top-0 -mt-1 -translate-y-full z-50 px-3 py-2 text-sm bg-gray-900 text-white rounded shadow-lg max-w-xs whitespace-normal">
            {initiative.title}
          </div>
        )}
      </div>

      {/* Meta row: status + spacer + indicators + owner */}
      <div className="flex items-center gap-2 mt-2 min-w-0">
        {status && (
          <div className="flex items-center gap-1 min-w-0">
            <StatusDot color={status.color} />
            <span className="text-xs text-text-secondary truncate">{status.name}</span>
          </div>
        )}

        <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
          {/* Tags */}
          {displayTags.slice(0, 3).map((initTag: any) => {
            const IconComponent = initTag.tag?.icon ? ICON_MAP[initTag.tag.icon] : null;
            if (!IconComponent) return null;
            return (
              <div key={initTag.tagId} className="group/tag relative">
                <IconComponent className="w-3.5 h-3.5 text-text-secondary" />
                <div className="absolute hidden group-hover/tag:block top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap z-[100]">
                  {initTag.tag?.name}
                </div>
              </div>
            );
          })}
          {displayTags.length > 3 && (
            <span className="text-xs text-text-tertiary">+{displayTags.length - 3}</span>
          )}

          {/* Dependency icon */}
          {hasDependencies && (
            <div className="group/dep relative">
              <LinkIcon className="w-3.5 h-3.5 text-text-secondary" />
              <div className="absolute hidden group-hover/dep:block top-full right-0 mt-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap z-[100]">
                {dependencyTooltip}
              </div>
            </div>
          )}

          {/* Comment count */}
          {commentCount > 0 && (
            <div className="group/comment relative flex items-center gap-0.5 text-text-secondary">
              <ChatBubbleLeftIcon className="w-3.5 h-3.5" />
              <span className="text-xs">{commentCount > 99 ? "99+" : commentCount}</span>
              <div className="absolute hidden group-hover/comment:block top-full right-0 mt-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap z-[100]">
                {commentCount} comment{commentCount !== 1 ? "s" : ""}
              </div>
            </div>
          )}

          {/* Owner avatar */}
          {owner?.name && (
            <div className="group/owner relative flex-shrink-0">
              <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[10px] font-medium">
                {getInitials(owner.name)}
              </div>
              <div className="absolute hidden group-hover/owner:block top-full right-0 mt-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap z-[100]">
                {owner.name}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Start / End — exact dates for date-mode, mapped months otherwise */}
      {dateRange ? (
        <div className="mt-1.5 text-[11px] text-text-tertiary whitespace-nowrap">
          {dateRange}
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 mt-1.5 text-[11px] text-text-tertiary whitespace-nowrap">
          <span>
            Start: {startLabel ? <MonthLabel value={startLabel} /> : "-"}
          </span>
          <span>
            End: {endLabel ? <MonthLabel value={endLabel} /> : "-"}
          </span>
        </div>
      )}
    </div>
  );
}
